/**
 * SF-4746: a demo that failed or was cancelled before anything was built can be dismissed (sf-api
 * decides, `can_dismiss`). POST .../dismiss: it leaves the creator's list and the record is kept.
 * 409 DEMO_BUILD_NOT_DISMISSABLE when it can no longer be (another tab dismissed it or drafted it
 * again): the page shows the build as it now is, like Draft again's repeat call.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Popconfirm, Space } from 'antd';
import {
  DEMO_BUILD_NOT_DISMISSABLE,
  demoErrorCode,
  demoErrorMessage,
  demoBuildsListKeyPrefix,
  dismissArgs,
  useDemoMutation,
  type DemoBuild,
} from '../api/demoBuildsClient';

export interface DismissDemoProps {
  buildId: string;
  /** Read the build again (when sf-api says it can no longer be dismissed). */
  onBuild: (buildId: string, answer?: DemoBuild) => Promise<void> | void;
}

export const DismissDemo: React.FC<DismissDemoProps> = ({ buildId, onBuild }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dismiss = useDemoMutation<DemoBuild>();
  const [error, setError] = useState<string | null>(null);

  const onDismiss = async () => {
    setError(null);
    try {
      await dismiss.mutateAsync(dismissArgs(buildId));
      // Every cached list page may still hold this demo: drop them, so the list reads afresh and
      // never shows it again from the cache (even if that read fails).
      queryClient.removeQueries({ queryKey: [...demoBuildsListKeyPrefix] });
      navigate('/demos');
    } catch (err) {
      if (demoErrorCode(err) === DEMO_BUILD_NOT_DISMISSABLE) {
        await onBuild(buildId);
        return;
      }
      setError(demoErrorMessage(err, 'The demo could not be dismissed. Try again.'));
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Popconfirm
        title="Dismiss this demo?"
        description="It leaves your list. The record is kept."
        okText="Yes, dismiss it"
        cancelText="Keep it"
        onConfirm={() => void onDismiss()}
      >
        <Button loading={dismiss.isLoading}>Dismiss</Button>
      </Popconfirm>
      {error && <Alert type="error" showIcon message={error} />}
    </Space>
  );
};
